import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import {
  LEAD_ALERT_LABELS,
  LEAD_CHANNEL_LABELS,
  LEAD_STATUS_LABELS,
  Lead,
  LeadChannel,
} from '../../types';
import {
  calcDaysWithoutContact,
  formatDateBR,
  formatPhoneBR,
  resolveLeadAlert,
} from './leadUtils';
import { LeadReportSummary } from './leadReportUtils';
import { LeadReportChartImages } from './leadChartImages';

const truncate = (v: unknown, max: number): string => {
  const s = String(v ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '—';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
};

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 14,
    gap: 12,
    alignItems: 'center',
  },
  logo: { width: 140, height: 36, objectFit: 'contain' },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#6B7280', fontSize: 9, lineHeight: 1.35 },
  totalsBox: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 10,
    marginBottom: 14,
  },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 5 },
  totalsLabel: { color: '#6B7280' },
  totalsValue: { fontWeight: 700 },
  sectionTitle: { fontSize: 12, fontWeight: 700, marginBottom: 8, marginTop: 4 },
  chartsRow: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  chartBox: {
    flexGrow: 1,
    flexBasis: 0,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 8,
    alignItems: 'center',
  },
  chartImage: { width: '100%', height: 210, objectFit: 'contain' },
  chartFull: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 8,
    marginBottom: 10,
    alignItems: 'center',
  },
  chartFullImage: { width: '100%', height: 230, objectFit: 'contain' },
  emptyChart: { color: '#9CA3AF', fontSize: 9, paddingVertical: 40 },
  table: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, overflow: 'hidden' },
  rowHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  row: {
    flexDirection: 'column',
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    paddingVertical: 7,
    paddingHorizontal: 8,
  },
  rowTop: { flexDirection: 'row', alignItems: 'flex-start' },
  cellName: { width: '22%', paddingRight: 4 },
  cellPhone: { width: '16%' },
  cellStatus: { width: '14%' },
  cellSeller: { width: '14%' },
  cellVehicle: { width: '18%', paddingRight: 4 },
  cellDate: { width: '16%' },
  metaText: { marginTop: 2, fontSize: 8, color: '#6B7280', lineHeight: 1.3 },
  footerNote: { marginTop: 10, color: '#6B7280', fontSize: 8, lineHeight: 1.35 },
});

export interface LeadsReportPDFProps {
  leads: Lead[];
  summary: LeadReportSummary;
  charts: LeadReportChartImages;
  generatedAtLabel: string;
  logoUrl?: string | null;
  filterLabel?: string;
  sellerNameById: (id: number | null | undefined) => string;
}

const LeadsReportPDF: React.FC<LeadsReportPDFProps> = ({
  leads,
  summary,
  charts,
  generatedAtLabel,
  logoUrl,
  filterLabel,
  sellerNameById,
}) => (
  <Document title="Relatório de leads">
    <Page size="A4" style={styles.page}>
      <View style={styles.headerRow}>
        {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
        <View style={{ flexGrow: 1, paddingHorizontal: 8 }}>
          <Text style={styles.title}>Relatório de leads</Text>
          <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
          {filterLabel ? <Text style={styles.subtitle}>{filterLabel}</Text> : null}
        </View>
        <View>
          <Text style={styles.subtitle}>{summary.total} lead(s)</Text>
        </View>
      </View>

      <View style={styles.totalsBox}>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Total de leads:</Text>
          <Text style={styles.totalsValue}>{summary.total}</Text>
        </View>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Ganhos:</Text>
          <Text style={styles.totalsValue}>{summary.won}</Text>
        </View>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Perdidos:</Text>
          <Text style={styles.totalsValue}>{summary.lost}</Text>
        </View>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Conversão (ganhos ÷ ganhos+perdidos):</Text>
          <Text style={styles.totalsValue}>
            {summary.conversionRate == null ? '—' : `${summary.conversionRate}%`}
          </Text>
        </View>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Alertas (atenção / urgente):</Text>
          <Text style={styles.totalsValue}>
            {summary.alertsAttention} / {summary.alertsUrgent}
          </Text>
        </View>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Sem vendedor:</Text>
          <Text style={styles.totalsValue}>{summary.withoutSeller}</Text>
        </View>
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Média de dias sem contato:</Text>
          <Text style={styles.totalsValue}>
            {summary.daysAvgWithoutContact == null ? '—' : summary.daysAvgWithoutContact}
          </Text>
        </View>
      </View>

      <Text style={styles.sectionTitle}>Gráficos</Text>
      <View style={styles.chartsRow}>
        <View style={styles.chartBox}>
          {charts.statusChart ? (
            <Image src={charts.statusChart} style={styles.chartImage} />
          ) : (
            <Text style={styles.emptyChart}>Sem dados de status</Text>
          )}
        </View>
        <View style={styles.chartBox}>
          {charts.originChart ? (
            <Image src={charts.originChart} style={styles.chartImage} />
          ) : (
            <Text style={styles.emptyChart}>Sem dados de origem</Text>
          )}
        </View>
      </View>
      <View style={styles.chartFull}>
        {charts.sellerChart ? (
          <Image src={charts.sellerChart} style={styles.chartFullImage} />
        ) : (
          <Text style={styles.emptyChart}>Sem dados de vendedor</Text>
        )}
      </View>

      <Text style={styles.footerNote}>
        Os gráficos consideram os filtros aplicados na tela no momento da exportação.
      </Text>
    </Page>

    <Page size="A4" style={styles.page}>
      <View style={styles.headerRow}>
        {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
        <View style={{ flexGrow: 1, paddingHorizontal: 8 }}>
          <Text style={styles.title}>Leads — detalhamento</Text>
          <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
        </View>
        <View>
          <Text style={styles.subtitle}>{leads.length} item(ns)</Text>
        </View>
      </View>

      <View style={styles.table}>
        <View style={styles.rowHeader}>
          <Text style={styles.cellName}>Nome</Text>
          <Text style={styles.cellPhone}>WhatsApp</Text>
          <Text style={styles.cellStatus}>Status</Text>
          <Text style={styles.cellSeller}>Vendedor</Text>
          <Text style={styles.cellVehicle}>Interesse</Text>
          <Text style={styles.cellDate}>Contatos</Text>
        </View>

        {leads.map((lead) => {
          const days = calcDaysWithoutContact(lead);
          const alert = resolveLeadAlert(lead);
          const origin =
            lead.origin != null
              ? LEAD_CHANNEL_LABELS[lead.origin as LeadChannel] ?? lead.origin
              : 'Sem origem';
          return (
            <View key={lead.id} style={styles.row} wrap={false}>
              <View style={styles.rowTop}>
                <Text style={styles.cellName}>{truncate(lead.name, 28)}</Text>
                <Text style={styles.cellPhone}>{formatPhoneBR(lead.whatsapp)}</Text>
                <Text style={styles.cellStatus}>
                  {LEAD_STATUS_LABELS[lead.status] ?? lead.status}
                </Text>
                <Text style={styles.cellSeller}>
                  {truncate(sellerNameById(lead.seller_id), 18)}
                </Text>
                <Text style={styles.cellVehicle}>
                  {truncate(lead.vehicle_interest || '—', 24)}
                </Text>
                <View style={styles.cellDate}>
                  <Text>Últ.: {formatDateBR(lead.last_contact_at)}</Text>
                  <Text style={styles.metaText}>Próx.: {formatDateBR(lead.next_contact_at)}</Text>
                </View>
              </View>
              <Text style={styles.metaText}>
                Origem: {origin}
                {alert !== 'nenhum' ? ` • Alerta: ${LEAD_ALERT_LABELS[alert]}` : ''}
                {days != null ? ` • ${days} dia(s) s/ contato` : ''}
                {lead.next_contact_action
                  ? ` • Próx. ação: ${truncate(lead.next_contact_action, 40)}`
                  : ''}
              </Text>
            </View>
          );
        })}
      </View>
    </Page>
  </Document>
);

export default LeadsReportPDF;
